import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Divider } from '@mui/material';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  Tooltip,
  Paper,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from '../../../components/snackbar';
import FormProvider from '../../../components/hook-form';
import FramesAndLenses from './Specs/FramesAndLenses';
import AddonAndVoucher from './Specs/AddonAndVoucher';
import FinalSummary from '../Patient/Refraction/FinalSummary';
import { IDispenseRequest } from '../../../@types/dispense';
import { Addon } from '../../../@types/addon.type';
import { Vouchers } from '../../../@types/voucher.type';
import { DispenseService } from '../../../api/dispense.service';
import DispenseTable from './DispenseTable';
import { useAppContext } from '../../../providers/AppContext';
import useBarcodeScanner from '../../../hooks/useBarcodeScanner';
import { ProductService } from '../../../api/product.service';
import { ProductType } from '../../../@types/product';
import AddPatient from '../../../sections/@dashboard/patient/AddPatient';
import DiscardFormDialog from '../../../components/discard-form-dialog';
import { IPatientSchema } from '../../../@types/patient';
import { patientDisplayName } from '../../../utils/patientName';
import { ClinicalRecords } from '../../../api/clinicalRecords.service';
import Iconify from '../../../components/iconify';
import NhsVoucherPicker, {
  NhsVoucherSelection,
} from '../../../components/dispense/NhsVoucherPicker';
import ClaimForDispenseDialog from '../../../components/claims/ClaimForDispenseDialog';
import { readPrescription } from '../../../components/claims/readPrescription';
import { buildTillFocusState } from '../Till/tillNavShared';
import { getApiErrorMessage } from '../../../utils/util';
import { fDate, parseDateOnly } from '../../../utils/formatTime';
import {
  createDiscardOnDialogCloseHandler,
  requestCloseWithUnsavedCheck,
} from '../../../utils/form-unsaved-close.util';
import {
  buildDispenseChangeSnapshot,
  dispenseSnapshotsEqual,
  DispenseChangeSnapshot,
  DispenseSnapshotInput,
  EMPTY_DISPENSE_SNAPSHOT_INPUT,
} from './dispense-change-snapshot.util';

const DISPENSE_OTHER_PRODUCTS = 'dispense_other_products';

const SIGNED_PRESCRIPTION_REQUIRED_MESSAGE =
  'This patient has no signed clinical record. Complete and sign the handover in Patient before creating a dispense.';

/** Map GET /dispense/:id `patient` for AddPatient pre-fill (external / full edit). */
function mapApiPatientToAddPatientForm(raw: any): IPatientSchema | undefined {
  if (!raw?.id) return undefined;
  const title = (raw.title ?? 'Mr').toString().trim() || 'Mr';
  const sur = (raw.surName ?? '').toString().trim();
  const salutation =
    (raw.lastName ?? '').toString().trim() ||
    [title, sur].filter(Boolean).join(' ').trim() ||
    title;
  return {
    ...raw,
    id: String(raw.id),
    title,
    firstName: raw.firstName ?? '',
    surName: sur,
    lastName: salutation,
    date_of_birth: parseDateOnly(raw.date_of_birth),
    mobile: (raw.phone ?? raw.mobile ?? '').toString(),
    phone: raw.phone ?? '',
    email: raw.email ?? '',
  } as IPatientSchema;
}

function isRxDispenseContext(type: string): boolean {
  return type === 'dispense_frames' || type === 'dispense_contact_lenses';
}

function isLineAllowedForDispenseMode(
  line: Record<string, unknown>,
  dispenseMode: string,
): boolean {
  const type = String(line.type ?? '');
  if (!dispenseMode || dispenseMode === DISPENSE_OTHER_PRODUCTS) return true;
  if (dispenseMode === 'dispense_frames') {
    return type !== ProductType.CONTACT_LENSES;
  }
  if (dispenseMode === 'dispense_contact_lenses') {
    return type !== ProductType.FRAMES;
  }
  return true;
}

function isBatchAwareLine(item: Record<string, unknown>): boolean {
  if (item.requiresLotTracking === true) return true;
  const t = String(item.type ?? '');
  return t === ProductType.CONTACT_LENSES || t === ProductType.SOLUTION;
}

/**
 * One basket line per product: plain (non lot-tracked) rows for the same
 * product are folded into a single row with a summed quantity. Lot-tracked rows
 * stay separate because each row carries its own lot / pack size.
 */
function mergeDuplicateProductLines<T>(lines: T[]): T[] {
  const merged: T[] = [];
  const indexByProduct = new Map<string, number>();
  for (const line of lines) {
    const row = line as Record<string, unknown>;
    const pid = row?.id ? String(row.id) : '';
    if (!pid || isBatchAwareLine(row)) {
      merged.push(line);
      continue;
    }
    const at = indexByProduct.get(pid);
    const qty = Math.max(1, Number(row.quantity) || 1);
    if (at === undefined) {
      indexByProduct.set(pid, merged.length);
      merged.push({ ...row, quantity: qty } as T);
      continue;
    }
    const existing = merged[at] as Record<string, unknown>;
    const quantity = Math.max(1, Number(existing.quantity) || 1) + qty;
    merged[at] = { ...existing, quantity, quantityIssued: quantity } as T;
  }
  return merged;
}

function getDispenseSubmitErrorMessage(err: unknown): string {
  return getApiErrorMessage(err, 'Error saving dispense');
}

function createInitialPickupTime(): string {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function SummaryFigure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="subtitle1">{value}</Typography>
    </Box>
  );
}

export default function DispensePage() {
  const { dispenseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [addons, setAddons] = useState<Addon[]>([]);
  const [vouchers, setVouchers] = useState<Vouchers[]>([]);
  const [date, setDate] = useState<Date | null>(new Date());
  const [time, setTime] = useState<string | null>(createInitialPickupTime);
  const [dispenseData, setDispenseData] = useState<any>(null);
  const [selectedDispense, setSelectedDispense] = useState<any>(null);
  const [selectedPatient, setSelectedPatient] = useState<
    number | string | null
  >(null);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [baselineSnapshot, setBaselineSnapshot] =
    useState<DispenseChangeSnapshot>(() =>
      buildDispenseChangeSnapshot(
        EMPTY_DISPENSE_SNAPSHOT_INPUT,
        new Date(),
        createInitialPickupTime(),
        0,
      ),
    );
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [confirmClosePatientForm, setConfirmClosePatientForm] = useState(false);
  const [patientFormDirty, setPatientFormDirty] = useState(false);

  /**
   * The NHS optical voucher applied to this sale.
   *
   * Kept outside the react-hook-form state on purpose: it is not a free-text
   * field the user types but a server-priced selection, and it must round-trip
   * as a nested object the API values itself. `null` means "no voucher", which
   * is also how the API is told to REMOVE one from an existing dispense.
   */
  const [nhsVoucher, setNhsVoucher] = useState<NhsVoucherSelection | null>(
    null,
  );
  const [nhsNetContribution, setNhsNetContribution] = useState<number | null>(
    null,
  );
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);

  const state = location.state as any;

  const methods = useForm<IDispenseRequest>({
    mode: 'onChange',
    defaultValues: {
      patientId: null,
      type: '',
      specType: '',
      pdRight: null,
      pdLeft: null,
      heightRight: null,
      heightLeft: null,
      bvd: null,
      pantoscopicTilt: null,
      wrapAngle: null,
      adjustedPrice: 0,
      totalPrice: 0,
      discount: 0,
      pickupDateTime: '',
      framesAndLenses: [],
      addons: [],
      vouchers: [],
      orderingNotes: '',
      handoverNotes: '',
      hobbies: '',
      isVDU: false,
      isSmoker: false,
      isDriver: false,
      isExternal: false,
      sourceType: state?.sourceType || undefined,
      sourceId: state?.sourceId || undefined,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = methods;

  const watchedFormValues = useWatch({ control });

  const currentSnapshot = useMemo(
    () =>
      buildDispenseChangeSnapshot(
        (watchedFormValues ?? getValues()) as DispenseSnapshotInput,
        date,
        time,
        manualDiscount,
      ),
    [watchedFormValues, date, time, manualDiscount, getValues],
  );

  const hasUnsavedChanges = useMemo(
    () => !dispenseSnapshotsEqual(currentSnapshot, baselineSnapshot),
    [currentSnapshot, baselineSnapshot],
  );

  const canSaveDraft = !isSubmitting && hasUnsavedChanges;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'framesAndLenses',
  });

  const watchedIsExternal = useWatch({ control, name: 'isExternal' });
  const watchedSpecType = useWatch({ control, name: 'specType' }) ?? '';
  const watchedDispenseMode = useWatch({ control, name: 'type' }) ?? '';

  const watchedFramesAndLenses = useWatch({ control, name: 'framesAndLenses' });
  const watchedAdjustedPrice = useWatch({ control, name: 'adjustedPrice' });
  const watchedAddonRows = useWatch({ control, name: 'addons' });
  const watchedVoucherRows = useWatch({ control, name: 'vouchers' });
  const watchedDiscount = useWatch({ control, name: 'discount' });

  // `addons` / `vouchers` state hold the catalogue options for the pickers;
  // the summary counts only the rows the user has actually chosen a value for.
  const productUnitCount = useMemo(
    () =>
      (Array.isArray(watchedFramesAndLenses) ? watchedFramesAndLenses : [])
        .filter((line) => !!line?.id)
        .reduce((sum: number, line) => {
          const row = line as Record<string, unknown>;
          const raw = isBatchAwareLine(row)
            ? (row.quantityIssued ?? row.quantity)
            : row.quantity;
          return sum + Math.max(1, Number(raw) || 1);
        }, 0),
    [watchedFramesAndLenses],
  );
  const selectedAddonCount = useMemo(
    () =>
      (Array.isArray(watchedAddonRows) ? watchedAddonRows : []).filter(
        (row: { addon?: unknown }) => !!row?.addon,
      ).length,
    [watchedAddonRows],
  );
  const selectedVoucherCount = useMemo(
    () =>
      (Array.isArray(watchedVoucherRows) ? watchedVoucherRows : []).filter(
        (row: { voucher?: unknown }) => !!row?.voucher,
      ).length,
    [watchedVoucherRows],
  );

  /**
   * The patient's refraction, read from the clinical record behind this
   * dispense. Sent to the voucher quote so the SERVER derives the band from the
   * same powers the GOS3 claim will later state — a band guessed in the UI
   * could differ from the one claimed, which is money.
   */
  const patientPrescription = useMemo(
    () =>
      readPrescription(
        (selectedDispense?.clinicalRecord?.refraction?.final ??
          dispenseData?.dispenses?.[0]?.clinicalRecord?.refraction?.final) as
          Record<string, unknown> | undefined,
      ),
    [selectedDispense, dispenseData],
  );

  const calculateFramesAndLensesPrice = () => {
    if (!watchedFramesAndLenses) return 0;
    return watchedFramesAndLenses.reduce((total: number, item: any) => {
      const price = parseFloat(item.price) || 0;
      const qty = isBatchAwareLine(item)
        ? Math.max(1, Number(item.quantityIssued ?? item.quantity) || 1)
        : Math.max(1, Number(item.quantity) || 1);
      return total + price * qty;
    }, 0);
  };

  const framesAndLensesPrice = calculateFramesAndLensesPrice();

  const { patients, getPatients } = useAppContext();

  const isNew = dispenseId === 'new' || !dispenseId;
  const isUpdatingExistingDispense = Boolean(selectedDispense?.id);

  const externalModalPatient = useMemo((): IPatientSchema | undefined => {
    if (isNew || !dispenseData?.patient) return undefined;
    return mapApiPatientToAddPatientForm(dispenseData.patient);
  }, [isNew, dispenseData?.patient]);

  const isExternalPatientEdit = Boolean(externalModalPatient?.id);

  const [allProducts, setAllProducts] = useState<any[]>([]);
  const sectionTitleSx = { mb: 2, fontWeight: 600 } as const;
  const clHandoverSummary = useMemo(() => {
    const handover = selectedDispense?.clinicalRecord?.contactLensHandover;
    if (!handover) return null;
    return {
      supplier: handover.supplier,
      type: handover.type,
      freq: handover.freq,
      lens: handover.lens,
      solution: handover.solution,
      packSize: handover.packSize,
      quantityIssued: handover.quantityIssued,
      visitType: handover.visitType,
      createdBy: handover.createdBy,
    };
  }, [selectedDispense]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await new ProductService().getActiveProducts();
        setAllProducts(data);
      } catch (error) {
        console.error('Failed to fetch products for scanner', error);
      }
    };
    fetchProducts();
  }, []);

  const handleScan = (barcode: string) => {
    const product = allProducts.find(
      (p) => p.code?.toLowerCase() === barcode.toLowerCase(),
    );

    if (product) {
      (append as any)({
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.salePrice ?? product.price ?? 0,
        category: product.category,
        code: product.code,
        quantity: 1,
        quantityIssued: 1,
        packSize: Math.max(1, Number(product.packSize ?? 1) || 1),
        differentRL: false,
        requiresLotTracking: Boolean(product.requiresLotTracking),
      });
      enqueueSnackbar(`Added ${product.name}`, { variant: 'success' });
    } else {
      enqueueSnackbar(`Product with code "${barcode}" not found`, {
        variant: 'warning',
      });
    }
  };

  useBarcodeScanner({ onScan: handleScan });

  useEffect(() => {
    if (!isNew) return;
    getPatients();
    const navState = location.state as { patientId?: string | number } | null;
    if (!navState?.patientId) return;

    const pid = navState.patientId;
    let cancelled = false;

    (async () => {
      if (getValues('isExternal')) {
        if (!cancelled) {
          setSelectedPatient(pid);
          setValue('patientId', pid, { shouldDirty: true });
        }
        return;
      }
      try {
        const { data } =
          await new ClinicalRecords().getPatientSignedForDispense(pid);
        if (cancelled) return;
        if (!data?.eligible) {
          enqueueSnackbar(SIGNED_PRESCRIPTION_REQUIRED_MESSAGE, {
            variant: 'error',
          });
          setSelectedPatient(null);
          setValue('patientId', null);
          return;
        }
        setSelectedPatient(pid);
        setValue('patientId', pid, { shouldDirty: true });
      } catch {
        if (cancelled) return;
        enqueueSnackbar(
          'Could not verify clinical records for this patient. Try again.',
          { variant: 'error' },
        );
        setSelectedPatient(null);
        setValue('patientId', null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isNew, location.state, getPatients, getValues, setValue]);

  const prevDispenseTypeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevDispenseTypeRef.current;
    if (
      watchedDispenseMode === DISPENSE_OTHER_PRODUCTS &&
      prev !== DISPENSE_OTHER_PRODUCTS
    ) {
      setValue('specType', '');
      setValue('pdRight', null);
      setValue('pdLeft', null);
      setValue('heightRight', null);
      setValue('heightLeft', null);
    }
    prevDispenseTypeRef.current = watchedDispenseMode;
  }, [watchedDispenseMode, setValue]);

  useEffect(() => {
    const lines = Array.isArray(getValues('framesAndLenses'))
      ? (getValues('framesAndLenses') as Record<string, unknown>[])
      : [];
    if (!lines.length) return;
    if (!watchedDispenseMode || watchedDispenseMode === DISPENSE_OTHER_PRODUCTS)
      return;

    const filtered = lines.filter((line: Record<string, unknown>) =>
      isLineAllowedForDispenseMode(line, watchedDispenseMode),
    );
    if (filtered.length === lines.length) return;

    setValue('framesAndLenses', filtered as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
    enqueueSnackbar(
      'Removed products that do not match selected dispense type.',
      {
        variant: 'warning',
      },
    );
  }, [watchedDispenseMode, getValues, setValue]);

  const onSubmit = async (data: IDispenseRequest) => {
    if (!data.patientId) {
      enqueueSnackbar('Please select a patient', { variant: 'error' });
      return;
    }
    if (!data.isExternal) {
      try {
        const { data: elig } =
          await new ClinicalRecords().getPatientSignedForDispense(
            data.patientId,
          );
        if (!elig?.eligible) {
          enqueueSnackbar(SIGNED_PRESCRIPTION_REQUIRED_MESSAGE, {
            variant: 'error',
          });
          return;
        }
      } catch {
        enqueueSnackbar(
          'Could not verify clinical records. Please try again.',
          { variant: 'error' },
        );
        return;
      }
    }
    if (!data.type) {
      enqueueSnackbar('Please select a dispense type', {
        variant: 'error',
      });
      return;
    }

    const rxFlow = isRxDispenseContext(data.type);
    if (rxFlow) {
      if (!data.specType) {
        enqueueSnackbar('Please select spec type', { variant: 'error' });
        return;
      }
      if (!data.pdRight || !data.pdLeft) {
        enqueueSnackbar('Please fill pupillary distance for both eyes', {
          variant: 'error',
        });
        return;
      }
      if (['Varifocal', 'Bifocal'].includes(data.specType)) {
        if (!data.heightRight || !data.heightLeft) {
          enqueueSnackbar(
            'Height measurements are required for Varifocal/Bifocal lenses',
            {
              variant: 'error',
            },
          );
          return;
        }
      }
    }

    if (!date || !time) {
      enqueueSnackbar('Please select pickup date and time', {
        variant: 'error',
      });
      return;
    }
    if (data.framesAndLenses.length === 0) {
      enqueueSnackbar('Please add at least one product line', {
        variant: 'error',
      });
      return;
    }

    for (let i = 0; i < data.framesAndLenses.length; i++) {
      const item = data.framesAndLenses[i] as Record<string, unknown>;
      if (!item.name || !item.type || !item.price || !item.category) {
        enqueueSnackbar(`Please fill all fields for product line ${i + 1}`, {
          variant: 'error',
        });
        return;
      }
      const needsLot = isBatchAwareLine(item);
      if (needsLot) {
        const hasLot = !!(item.goodsInId && String(item.goodsInId).trim());
        if (!hasLot) {
          enqueueSnackbar(
            `Select a received lot for line ${i + 1} (lot-tracked product).`,
            { variant: 'error' },
          );
          return;
        }
        const packSize = Math.max(
          1,
          Math.floor(Number(item.packSize ?? 1) || 1),
        );
        const quantityIssued = Math.max(
          1,
          Math.floor(Number(item.quantityIssued ?? item.quantity ?? 1) || 1),
        );
        if (!packSize || !quantityIssued) {
          enqueueSnackbar(
            `Pack size and quantity issued are required for line ${i + 1}.`,
            { variant: 'error' },
          );
          return;
        }
      }
    }

    // Inside onSubmit, as the first check:
    const toMoney = (value: unknown): number => {
      const parsed =
        value === null || value === undefined || value === ''
          ? 0
          : Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const roundMoney = (value: number): number =>
      Math.round((value + Number.EPSILON) * 100) / 100;

    const currentDiscount = toMoney(methods.getValues('discount'));
    const currentTotal = toMoney(methods.getValues('totalPrice'));
    if (currentDiscount > currentTotal) {
      enqueueSnackbar('Discount cannot exceed the total price', {
        variant: 'error',
      });
      return;
    }
    const framesAndLensesPayload = data.framesAndLenses
      .map((item: any) => {
        const quantity = Math.max(1, Number(item.quantity) || 1);
        const isLotTracked = isBatchAwareLine(item);
        const packSize = isLotTracked
          ? Math.max(1, Math.floor(Number(item.packSize ?? 1) || 1))
          : undefined;
        const quantityIssued = isLotTracked
          ? Math.max(
              1,
              Math.floor(Number(item.quantityIssued ?? quantity) || 1),
            )
          : undefined;
        return {
          productId: String(item.id),
          quantity: isLotTracked ? packSize! * quantityIssued! : quantity,
          packSize,
          quantityIssued,
          goodsInId: item.goodsInId
            ? String(item.goodsInId).trim() || undefined
            : undefined,
        };
      })
      .filter((row: { productId: string }) => !!row.productId);

    const addonIds = data.addons
      .map((item: any) => Number(item.addon))
      .filter((id: number) => !isNaN(id) && id !== 0);

    const voucherIds = data.vouchers
      .map((item: any) => Number(item.voucher))
      .filter((id: number) => !isNaN(id) && id !== 0);

    const pickupDateTime =
      date && time
        ? new Date(
            `${date.toISOString().split('T')[0]}T${time}:00`,
          ).toISOString()
        : '';

    const dispensePayload = {
      ...data,
      patientId: data.patientId,
      status: 'draft',
      framesAndLenses: framesAndLensesPayload,
      addons: addonIds,
      vouchers: voucherIds,
      pickupDateTime,
      // The server values the voucher against the published rate table and
      // reduces what the till asks the patient for. `null` removes a voucher
      // from a dispense that previously carried one.
      nhsVoucher: nhsVoucher?.type ? nhsVoucher : null,
      adjustedPrice: roundMoney(toMoney(data.adjustedPrice)),
      totalPrice: roundMoney(toMoney(data.totalPrice)),
      discount: roundMoney(toMoney(data.discount)),

      pdRight: data.pdRight ? Number(data.pdRight) : null,
      pdLeft: data.pdLeft ? Number(data.pdLeft) : null,
      heightRight: data.heightRight ? Number(data.heightRight) : null,
      heightLeft: data.heightLeft ? Number(data.heightLeft) : null,
      bvd: data.bvd ? Number(data.bvd) : null,
      pantoscopicTilt: data.pantoscopicTilt
        ? Number(data.pantoscopicTilt)
        : null,
      wrapAngle: data.wrapAngle ? Number(data.wrapAngle) : null,
    };

    try {
      let response: any;
      if (selectedDispense?.id) {
        response = await new DispenseService().updateDispense(
          selectedDispense.id,
          dispensePayload as any,
        );
        console.log('RESPONSE', response);
        enqueueSnackbar(
          'Dispense Updated Successfully!.... Redirecting to Till',
          {
            variant: 'success',
          },
        );
      } else {
        response = await new DispenseService().createDispense(
          dispensePayload as any,
        );
        console.log('RESPONSE', response);
        enqueueSnackbar(
          'Draft Dispense and Till Created Successfully!.... Redirecting to Till',
          {
            variant: 'success',
          },
        );
      }

      const savedSnapshot = buildDispenseChangeSnapshot(
        getValues(),
        date,
        time,
        manualDiscount,
      );
      reset(getValues(), { keepDefaultValues: false });
      setBaselineSnapshot(savedSnapshot);

      setTimeout(() => {
        const tillId = response?.data?.till?.id;
        // A till holds one row per dispense for the patient — tell the Till page
        // which row to select, otherwise it defaults to the patient's oldest one.
        const savedDispenseId =
          response?.data?.dispense?.id ?? selectedDispense?.id ?? null;

        if (tillId) {
          navigate(`/till/${tillId}`, {
            state: buildTillFocusState(savedDispenseId),
          });
        } else {
          navigate(-1); // Go back?
        }
      }, 2000);
    } catch (err: unknown) {
      console.error('Error creating dispense:', err);
      enqueueSnackbar(getDispenseSubmitErrorMessage(err), {
        variant: 'error',
      });
    }
  };

  const handleExternalPatientAdded = (payload?: any) => {
    const patient = payload?.data ?? payload;
    if (patient?.id) {
      setSelectedPatient(patient.id);
      methods.setValue('patientId', patient.id, { shouldDirty: true });
    }
    setOpenPatientModal(false);
    getPatients();
  };

  const handleAddPatientModalClose = () => {
    setOpenPatientModal(false);
    setPatientFormDirty(false);
    setConfirmClosePatientForm(false);
    const pid = methods.getValues('patientId');
    if (pid == null || pid === '') {
      methods.setValue('isExternal', false);
    }
  };

  const attemptClosePatientModal = () => {
    requestCloseWithUnsavedCheck(
      patientFormDirty,
      handleAddPatientModalClose,
      () => setConfirmClosePatientForm(true),
    );
  };

  const fetchDispenseData = async () => {
    if (isNew) {
      reset();
      setDispenseData(null);
      setSelectedDispense(null);
      setBaselineSnapshot(
        buildDispenseChangeSnapshot(
          EMPTY_DISPENSE_SNAPSHOT_INPUT,
          date,
          time,
          0,
        ),
      );
      return;
    }

    try {
      const res = await new DispenseService().getDispense(+dispenseId);
      setDispenseData(res.data);

      if (res.data.dispenses?.length > 0) {
        const idNum = Number(dispenseId);
        const match = res.data.dispenses.find(
          (d: any) => Number(d.id) === idNum,
        );
        setSelectedDispense(match ?? res.data.dispenses[0]);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch dispense data.', { variant: 'error' });
      console.error(error);
    }
  };

  function mapDispenseToForm(dispense: any) {
    const voucherDiscount =
      dispense.vouchers?.reduce(
        (sum: number, v: any) => sum + (v.price || 0),
        0,
      ) || 0;

    const manualDiscount = Math.max(
      0,
      (dispense.discount || 0) - voucherDiscount,
    );

    return {
      formValues: {
        patientId: dispense.patientId ?? '',
        type: dispense.type ?? '',
        specType: dispense.specType ?? '',
        pdRight: dispense.pdRight ?? null,
        pdLeft: dispense.pdLeft ?? null,
        heightRight: dispense.heightRight ?? null,
        heightLeft: dispense.heightLeft ?? null,
        bvd: dispense.bvd ?? null,
        pantoscopicTilt: dispense.pantoscopicTilt ?? null,
        wrapAngle: dispense.wrapAngle ?? null,
        pickupDateTime: dispense.pickupDateTime ?? '',
        adjustedPrice: dispense.adjustedPrice ?? 0,
        totalPrice: dispense.totalPrice ?? 0,
        discount: dispense.discount ?? 0,
        framesAndLenses: (() => {
          const rawLines =
            dispense.productLines?.length > 0
              ? dispense.productLines
              : (dispense.products ?? []).map((p: any) => ({
                  product: p,
                  quantity: 1,
                  quantityIssued: 1,
                  packSize: 1,
                  goodsInId: undefined,
                  batchNumber: undefined,
                }));
          return mergeDuplicateProductLines<
            IDispenseRequest['framesAndLenses'][number]
          >(
            rawLines.map((line: any) => {
              const p = line.product ?? line;
              return {
                id: p.id,
                name: p.name,
                type: p.type,
                price: p.salePrice ?? p.price ?? 0,
                category: p.category,
                goodsInId: line.goodsInId ?? '',
                batchNumber: line.batchNumber ?? '',
                quantity: line.quantity ?? 1,
                quantityIssued: line.quantityIssued ?? line.quantity ?? 1,
                packSize: line.packSize ?? p.packSize ?? 1,
                requiresLotTracking: Boolean(
                  p.requiresLotTracking ??
                  (p.type === ProductType.CONTACT_LENSES ||
                    p.type === ProductType.SOLUTION),
                ),
              };
            }),
          );
        })(),
        addons:
          dispense.addons?.map((a: any) => ({
            addon: a.id,
            discount: 0,
          })) ?? [],
        vouchers:
          dispense.vouchers?.map((v: any) => ({
            voucher: v.id,
            discount: v.price,
          })) ?? [],
        orderingNotes: dispense.orderingNotes ?? '',
        // Dispenses raised before handover notes were copied across (or whose
        // field was never filled) show the clinician's handover notes.
        handoverNotes:
          (typeof dispense.handoverNotes === 'string' &&
          dispense.handoverNotes.trim()
            ? dispense.handoverNotes
            : // The dispense column holds 16,000 characters (backend
              // DISPENSE_HANDOVER_NOTES_MAX); the handover allows more.
              dispense.clinicalRecord?.handover?.notes?.slice(0, 16000)) ?? '',
        hobbies:
          dispense.hobbies ??
          dispense.clinicalRecord?.prescreening?.hobbies ??
          '',
        isDriver:
          dispense.isDriver ??
          dispense.clinicalRecord?.prescreening?.isDriver ??
          false,
        isVDU:
          dispense.isVDU ??
          dispense.clinicalRecord?.prescreening?.isVduUse ??
          false,
        isSmoker:
          dispense.isSmoker ??
          dispense.clinicalRecord?.prescreening?.isSmoker ??
          false,
        isExternal: dispense.isExternal ?? false,
      },
      manualDiscount,
    };
  }

  function extractDateTime(pickupDateTime?: string) {
    if (!pickupDateTime) return null;

    const d = new Date(pickupDateTime);

    return {
      date: d,
      time: d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    };
  }

  const handlePatientAdded = (newPatient: any) => {
    if (!newPatient?.id) return;
    setSelectedPatient(newPatient.id);
    methods.setValue('patientId', newPatient.id, { shouldDirty: true });
    // Refresh patients list so the new one appears in the dropdown
    getPatients();
    setOpenPatientModal(false);
  };

  useEffect(() => {
    fetchDispenseData();
  }, [dispenseId]);

  useEffect(() => {
    if (!selectedDispense) return;

    const dateTime = extractDateTime(selectedDispense.pickupDateTime);
    let loadedDate = date;
    let loadedTime = time;
    if (dateTime) {
      loadedDate = dateTime.date;
      loadedTime = dateTime.time;
      setDate(loadedDate);
      setTime(loadedTime);
    }

    const { formValues, manualDiscount: loadedManualDiscount } =
      mapDispenseToForm(selectedDispense);

    setManualDiscount(loadedManualDiscount);
    setBaselineSnapshot(
      buildDispenseChangeSnapshot(
        formValues,
        loadedDate,
        loadedTime,
        loadedManualDiscount,
      ),
    );
    // Restore the NHS voucher the sale was priced with, so re-saving an
    // existing dispense does not silently strip it and re-charge the patient.
    setNhsVoucher(
      selectedDispense.nhsVoucherType
        ? {
            type: selectedDispense.nhsVoucherType,
            supplements: selectedDispense.nhsVoucherSupplements ?? [],
            lensesAffected: selectedDispense.nhsVoucherLenses ?? 2,
          }
        : null,
    );

    reset(formValues, { keepDefaultValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when selected row changes
  }, [selectedDispense, reset]);

  useEffect(() => {
    const fetchPatientDispenses = async () => {
      if (!selectedPatient) return;

      try {
        const res = await new DispenseService().getDispenseByPatientId(
          selectedPatient,
        );

        if (res.data && (res.data.dispenses || res.data.patient)) {
          setDispenseData(res.data);
        } else {
          setDispenseData(null);
          return;
        }

        const emptyPatientForm = {
          ...EMPTY_DISPENSE_SNAPSHOT_INPUT,
          patientId: selectedPatient,
          sourceType: state?.sourceType || undefined,
          sourceId: state?.sourceId || undefined,
          adjustedPrice: 0,
          totalPrice: 0,
          discount: 0,
          pickupDateTime: '',
        };
        reset(emptyPatientForm);
        setBaselineSnapshot(
          buildDispenseChangeSnapshot(
            {
              ...EMPTY_DISPENSE_SNAPSHOT_INPUT,
              patientId: selectedPatient,
              sourceType: state?.sourceType || undefined,
              sourceId: state?.sourceId || undefined,
            },
            date,
            time,
            0,
          ),
        );
      } catch (error: any) {
        const isNoDispenseError =
          error.response?.status === 404 ||
          error.message?.includes('not found') ||
          error.message?.includes('No dispense');

        if (!isNoDispenseError) {
          enqueueSnackbar('Failed to fetch patient dispense data.', {
            variant: 'error',
          });
          console.error(error);
        } else {
          enqueueSnackbar('Patient has no existing dispenses', {
            variant: 'warning',
          });
          setDispenseData(null);
        }
      }
    };

    if (isNew && selectedPatient) {
      fetchPatientDispenses();
    }
  }, [isNew, selectedPatient, reset]);

  useEffect(() => {
    if (watchedIsExternal) {
      setOpenPatientModal(true);
    } else {
      setSelectedPatient(null);
      methods.setValue('patientId', null);
    }
  }, [watchedIsExternal]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Typography
        variant="h4"
        sx={{
          p: 2,
          mb: 4,
          fontWeight: 700,
          color: 'primary.main',
          borderBottom: '3px solid',
          borderColor: 'primary.main',
        }}
      >
        {isUpdatingExistingDispense ? 'Update Dispense' : 'Add New Dispense'}
      </Typography>
      {dispenseData && (
        <DispenseTable
          dispenseData={dispenseData}
          selectedDispense={selectedDispense}
          onSelect={setSelectedDispense}
        />
      )}
      <Box sx={{ px: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'start', width: '100%' }}>
          <Box
            sx={{
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              mr: 2,
              mt: '2px',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'divider',
                pb: 1,
              }}
            >
              Patient Information
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '130%',
              }}
            >
              <Controller
                name="patientId"
                control={control}
                rules={{ required: 'Patient selection is required' }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Patient"
                    {...field}
                    onChange={async (e) => {
                      const value = e.target.value;
                      if (!value) {
                        field.onChange(value);
                        setSelectedPatient(null);
                        return;
                      }
                      if (isNew && !getValues('isExternal')) {
                        try {
                          const { data } =
                            await new ClinicalRecords().getPatientSignedForDispense(
                              value,
                            );
                          if (!data?.eligible) {
                            enqueueSnackbar(
                              SIGNED_PRESCRIPTION_REQUIRED_MESSAGE,
                              {
                                variant: 'error',
                              },
                            );
                            field.onChange('');
                            setSelectedPatient(null);
                            setValue('patientId', null);
                            return;
                          }
                        } catch {
                          enqueueSnackbar(
                            'Could not verify clinical records. Try again.',
                            { variant: 'error' },
                          );
                          field.onChange('');
                          setSelectedPatient(null);
                          setValue('patientId', null);
                          return;
                        }
                      }
                      field.onChange(value);
                      setSelectedPatient(value);
                    }}
                    value={field.value || ''}
                    error={!!errors.patientId}
                    helperText={
                      errors.patientId?.message ??
                      'Select the patient receiving this dispense'
                    }
                    required
                  >
                    {isNew
                      ? patients.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {`${p.name}`}
                          </MenuItem>
                        ))
                      : dispenseData?.patient && (
                          <MenuItem value={dispenseData.patient.id}>
                            {patientDisplayName(dispenseData.patient)}
                          </MenuItem>
                        )}
                  </TextField>
                )}
              />
              <Box
                sx={{
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  borderRadius: '8px',
                  mr: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                }}
              >
                <Controller
                  name="isVDU"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="VDU User"
                      labelPlacement="start"
                    />
                  )}
                />
                <Controller
                  name="isSmoker"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Smoker"
                      labelPlacement="start"
                    />
                  )}
                />
                <Controller
                  name="isDriver"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Driver"
                      labelPlacement="start"
                    />
                  )}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '70%',
              }}
            >
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Dispense type is required' }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Dispense Type"
                    {...field}
                    value={field.value || ''}
                    error={!!errors.type}
                    helperText={
                      errors.type?.message ?? 'Choose what is being dispensed'
                    }
                    required
                    fullWidth
                  >
                    <MenuItem value="dispense_frames">Dispense Frames</MenuItem>
                    <MenuItem value="dispense_contact_lenses">
                      Dispense Contact Lenses
                    </MenuItem>
                    <MenuItem value={DISPENSE_OTHER_PRODUCTS}>
                      Dispense Others
                    </MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="hobbies"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Lifestyle / Hobbies"
                    placeholder="e.g. driving, reading, sport"
                    size="small"
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              width: '15%',
              mt: 1,
            }}
          >
            <Controller
              name="isExternal"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch checked={field.value} onChange={field.onChange} />
                  }
                  label="External Patient"
                  labelPlacement="start"
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: '100%', mt: 2, px: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'divider',
                pb: 1,
                mb: 2,
              }}
            >
              Final Prescription
            </Typography>

            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <FinalSummary
                dense
                finalData={
                  selectedDispense?.clinicalRecord?.refraction?.final || {}
                }
              />
            </Box>
          </Box>
          {watchedDispenseMode === 'dispense_contact_lenses' &&
            clHandoverSummary && (
              <Box>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Contact Lens Handover
                </Typography>
                <Box
                  sx={{
                    px: 6,
                    py: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(220px, 1fr))',
                      },
                      gap: 3,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Typography variant="body2">
                        <strong>Visit Type:</strong>{' '}
                        {clHandoverSummary.visitType || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Supplier:</strong>{' '}
                        {clHandoverSummary.supplier || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Lens:</strong> {clHandoverSummary.lens || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Solution:</strong>{' '}
                        {clHandoverSummary.solution || '-'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Typography variant="body2">
                        <strong>Type:</strong> {clHandoverSummary.type || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Frequency:</strong>{' '}
                        {clHandoverSummary.freq || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pack Size:</strong>{' '}
                        {clHandoverSummary.packSize != null
                          ? clHandoverSummary.packSize
                          : '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Quantity Issued:</strong>{' '}
                        {clHandoverSummary.quantityIssued != null
                          ? clHandoverSummary.quantityIssued
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          <Box>
            <Typography
              variant="h5"
              sx={{
                ...sectionTitleSx,
                fontWeight: 600,
                mt: 3,
              }}
            >
              Notes
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '100%',

                p: 3,

                border: '1px solid',

                borderColor: 'divider',

                borderRadius: 2,

                backgroundColor: 'background.paper',

                boxShadow: 1,
              }}
            >
              <Controller
                name="handoverNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Handover Notes"
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
              <Controller
                name="orderingNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Ordering Notes"
                    multiline
                    rows={4}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {isRxDispenseContext(watchedDispenseMode) && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 4, px: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'divider',
                pb: 1,
                mb: 2,
              }}
            >
              Pupillary Distance
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                width: '100%',
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 4,
                  width: '40%',
                }}
              >
                <Typography sx={{ mr: 2 }}>Right</Typography>
                <Controller
                  name="pdRight"
                  control={control}
                  rules={{
                    min: {
                      value: 20,
                      message: 'PD must be at least 20 mm',
                    },
                    max: {
                      value: 40,
                      message: 'PD cannot exceed 40 mm',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const input = e.target.value;

                        const value: number | null =
                          input === '' ? null : Number(input);

                        field.onChange(value);

                        setValue('pdLeft', value);
                      }}
                      error={!!errors.pdRight}
                      helperText={errors.pdRight?.message}
                      required
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 4,
                  width: '40%',
                }}
              >
                <Typography sx={{ mr: 2 }}>Left</Typography>
                <Controller
                  name="pdLeft"
                  control={control}
                  rules={{
                    min: {
                      value: 20,
                      message: 'PD must be at least 20 mm',
                    },
                    max: {
                      value: 40,
                      message: 'PD cannot exceed 40 mm',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const input = e.target.value;
                        const value: number | null =
                          input === '' ? null : Number(input);
                        field.onChange(value);
                      }}
                      error={!!errors.pdLeft}
                      helperText={errors.pdLeft?.message}
                      required
                    />
                  )}
                />
              </Box>
              <Controller
                name="specType"
                control={control}
                rules={{ required: 'Spec type selection is required' }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Spectacle Type"
                    {...field}
                    value={field.value || ''}
                    sx={{ minWidth: 200 }}
                    error={!!errors.specType}
                    helperText={errors.specType?.message}
                    required
                  >
                    <MenuItem value="Distance">Distance</MenuItem>
                    <MenuItem value="Near">Near</MenuItem>
                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                    <MenuItem value="Bifocal">Bifocal</MenuItem>
                    <MenuItem value="Varifocal">Varifocal</MenuItem>
                  </TextField>
                )}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
              mb: 2,
              px: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
              }}
            >
              Additional Measurements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Height measurements are only required for Varifocal and Bifocal
              lenses.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Controller
                name="heightRight"
                control={control}
                rules={{
                  min: {
                    value: 10,
                    message: 'Height must be at least 10 mm',
                  },
                  max: {
                    value: 30,
                    message: 'Height cannot exceed 30 mm',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="number"
                    label="Right Height"
                    size="small"
                    disabled={
                      watchedSpecType !== 'Varifocal' &&
                      watchedSpecType !== 'Bifocal'
                    }
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? null : Number(e.target.value),
                      )
                    }
                    inputProps={{ max: 300 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="heightLeft"
                control={control}
                rules={{
                  min: {
                    value: 10,
                    message: 'Height must be at least 10 mm',
                  },
                  max: {
                    value: 30,
                    message: 'Height cannot exceed 30 mm',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="number"
                    label="Left Height"
                    size="small"
                    disabled={
                      watchedSpecType !== 'Varifocal' &&
                      watchedSpecType !== 'Bifocal'
                    }
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? null : Number(e.target.value),
                      )
                    }
                    inputProps={{ max: 300 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </Box>
        </>
      )}
      <Divider sx={{ my: 5 }} />
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        Products
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add products by searching or scanning a barcode. Review quantities
        before saving.
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          backgroundColor: 'background.paper',
          boxShadow: 1,
        }}
      >
        <FramesAndLenses
          specType={watchedSpecType}
          dispenseMode={watchedDispenseMode}
          fields={fields}
          append={append}
          remove={remove}
          disabled={false}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mt: 4,
          mb: 2,
        }}
      >
        Add-ons & Pickup
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          backgroundColor: 'background.paper',
          boxShadow: 1,
        }}
      >
        <AddonAndVoucher
          control={control}
          addons={addons}
          vouchers={vouchers}
          setAddons={setAddons}
          setVouchers={setVouchers}
          disabled={false}
          date={date}
          setDate={setDate}
          time={time}
          setTime={setTime}
          framesAndLensesPrice={framesAndLensesPrice}
          initialManualDiscount={manualDiscount}
          onManualDiscountChange={setManualDiscount}
        />

        <Divider sx={{ my: 3 }} />

        <NhsVoucherPicker
          chargedAmount={Number(watchedAdjustedPrice) || 0}
          value={nhsVoucher}
          onChange={setNhsVoucher}
          prescription={patientPrescription}
          // Local calendar day; toISOString() shifted it a day back east of UTC.
          suppliedDate={date ? fDate(date, 'yyyy-MM-dd') : undefined}
          onNetChange={setNhsNetContribution}
        />

        {/* Raising the GOS3 needs a saved dispense to hang off — the claim is
            built from what was actually recorded against the sale. */}
        {isUpdatingExistingDispense && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="mdi:file-document-check-outline" />}
              onClick={() => setClaimDialogOpen(true)}
            >
              Raise GOS Claim
            </Button>
          </Box>
        )}
      </Box>
      <Paper variant="outlined" sx={{ mt: 3, p: 2.5, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Dispense Summary
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          <SummaryFigure label="Products" value={String(productUnitCount)} />
          <SummaryFigure label="Add-ons" value={String(selectedAddonCount)} />
          <SummaryFigure
            label="Vouchers"
            value={String(selectedVoucherCount)}
          />
          <SummaryFigure
            label="Collection"
            value={
              date
                ? `${fDate(date, 'dd/MM/yyyy')}${time ? ` ${time}` : ''}`
                : '—'
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1} sx={{ ml: { md: 'auto' }, maxWidth: { md: 360 } }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Discount
            </Typography>
            <Typography variant="body2">
              {`£${Number(watchedDiscount || 0).toFixed(2)}`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle1">Total</Typography>
            <Typography variant="subtitle1">
              {`£${Number(
                watchedAdjustedPrice ?? getValues('totalPrice') ?? 0,
              ).toFixed(2)}`}
            </Typography>
          </Stack>
          {/* What the till will actually ask for. The voucher is settled by
              PCSE, not at the counter, so it is not part of "Total". */}
          {nhsVoucher?.type && nhsNetContribution != null && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Patient pays after NHS voucher
              </Typography>
              <Typography variant="subtitle2">
                {`£${nhsNetContribution.toFixed(2)}`}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 3 }}>
        <Tooltip
          title={
            canSaveDraft
              ? ''
              : isUpdatingExistingDispense
                ? 'No changes to update'
                : 'No changes to save'
          }
          disableHoverListener={canSaveDraft}
        >
          <span>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!canSaveDraft}
              size="large"
              startIcon={<Iconify icon="eva:save-outline" />}
              sx={{ px: 4 }}
            >
              {isUpdatingExistingDispense ? 'Update Draft' : 'Save Draft'}
            </LoadingButton>
          </span>
        </Tooltip>
        {/* <LoadingButton type="button" variant="outlined" sx={{ my: 2, mx: 1 }} disabled={!isValid}> */}
        {/* <LoadingButton type="button" variant="outlined" sx={{ my: 2, mx: 1 }}>
          Send to Till
        </LoadingButton> */}
      </Stack>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openPatientModal}
        onClose={createDiscardOnDialogCloseHandler(
          patientFormDirty,
          handleAddPatientModalClose,
          () => setConfirmClosePatientForm(true),
        )}
      >
        <DialogTitle>
          {isExternalPatientEdit ? 'Update Patient' : 'Add Patient'}
        </DialogTitle>
        <AddPatient
          key={`ext-patient-${externalModalPatient?.id ?? 'new'}-${openPatientModal}`}
          onSubmitForm={handleExternalPatientAdded}
          Fields={false}
          isEdit={isExternalPatientEdit}
          currentPatient={externalModalPatient}
          onCancel={attemptClosePatientModal}
          onCloseAfterSave={handleAddPatientModalClose}
          onDirtyStateChange={setPatientFormDirty}
        />
      </Dialog>
      <DiscardFormDialog
        open={confirmClosePatientForm}
        onClose={() => setConfirmClosePatientForm(false)}
        onDiscard={handleAddPatientModalClose}
      />
      {selectedDispense?.id && (
        <ClaimForDispenseDialog
          open={claimDialogOpen}
          onClose={() => setClaimDialogOpen(false)}
          dispenseId={Number(selectedDispense.id)}
          patientName={[
            dispenseData?.patient?.firstName,
            dispenseData?.patient?.surName,
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
    </FormProvider>
  );
}
